'use client';

import * as React from 'react';
import { cva } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { REGEX_PATTERNS } from '@/shared/config/common/constants/patterns';
import { cn } from '@/shared/lib/classnames/cn';
import type { VariantProps } from 'class-variance-authority';
const inputVariants = cva('rounded-2xl bg-bgSecondary text-textPrimary text-base placeholder:text-textSecondary focus-visible:outline-primary', {
  variants: {
    variant: {
      default: 'p-4',
      leftIcon: 'py-4 pr-4 pl-12'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
type ValidationType = {
  type: 'minlength';
  num: number;
} | {
  type: 'maxlength';
  num: number;
} | {
  type: 'isDigit';
  num: number;
} | {
  type: 'words-count';
  min: number;
} | {
  type: 'pattern';
  pattern: string | RegExp;
};
interface ValidationInput {
  settings: ValidationType;
  message: string;
}
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement>, VariantProps<typeof inputVariants> {
  validations?: ValidationInput[];
}
const validators = {
  'maxlength': (value: string, num: number): boolean => value.length <= num,
  'minlength': (value: string, num: number): boolean => value.length >= num,
  'isDigit': (value: string): boolean => REGEX_PATTERNS.IS_DIGIT.test(value),
  'words-count': (value: string, min: number): boolean => value.trim().split(/\s+/).length >= min,
  'pattern': (value: string, pattern: string | RegExp): boolean => {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    return !regex.test(value);
  }
};
const validationStrategies: Record<ValidationType['type'], (value: string, settings: ValidationType) => boolean> = {
  'maxlength': (value, settings) => validators.maxlength(value, (settings as {
    num: number;
  }).num),
  'minlength': (value, settings) => validators.minlength(value, (settings as {
    num: number;
  }).num),
  'isDigit': value => validators.isDigit(value),
  'words-count': (value, settings) => validators['words-count'](value, (settings as {
    min: number;
  }).min),
  'pattern': (value, settings) => validators.pattern(value, (settings as {
    pattern: string | RegExp;
  }).pattern)
};
const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  className,
  variant,
  validations,
  children,
  onChange,
  onFocus,
  onBlur,
  type,
  ...props
}, ref) => {
  const t = useTranslations();
  const [error, setError] = React.useState<string>('');
  const [focused, setFocused] = React.useState<boolean>(false);
  const validateInput = (value: string): string => {
    if (!validations || !value.length) return '';
    const failedValidation = validations.find(validation => !validationStrategies[validation.settings.type](value, validation.settings));
    return failedValidation ? failedValidation.message : '';
  };
  const preOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const validationError = validateInput(value);
    setError(validationError);
    if (onChange && typeof onChange === 'function') {
      onChange(e);
    }
  };
  const preOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    if (onBlur && typeof onBlur === 'function') {
      onBlur(e);
    }
  };
  const preOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    setError('');
    if (onFocus && typeof onFocus === 'function') {
      onFocus(e);
    }
  };
  return <div className="flex w-full flex-col">
        <div className="relative w-full px-1">
          {variant === 'leftIcon' && <>{children}</>}
          <input type={type} className={cn(inputVariants({
        variant,
        className
      }))} onChange={preOnChange} onFocus={preOnFocus} onBlur={preOnBlur} ref={ref} {...props} />
        </div>

        {error && !focused && <p className="pl-1 text-left text-base text-statusError">{t(error)}</p>}
      </div>;
});
Input.displayName = 'Input';
export { Input };
export type { ValidationInput };